import Seo from "@/components/Seo";
import { Reveal, FadeIn } from "@/components/Motion";
import { FIRM } from "@/data/site";

function LegalHero({ eyebrow, title }) {
  return (
    <section className="relative overflow-hidden bg-navy-deep pb-16 pt-40 lg:pb-20 lg:pt-52">
      <div className="grain absolute inset-0" aria-hidden="true" />
      <div className="container-x relative max-w-3xl">
        <FadeIn y={16}>
          <p className="eyebrow flex items-center gap-4">
            <span aria-hidden="true" className="h-px w-10 bg-gold" />
            {eyebrow}
          </p>
          <h1 className="mt-7 font-serif text-4xl font-medium text-white sm:text-5xl">
            {title}
          </h1>
        </FadeIn>
      </div>
    </section>
  );
}

function Section({ title, children }) {
  return (
    <Reveal>
      <section className="mt-12">
        <h2 className="font-serif text-2xl font-semibold text-navy">{title}</h2>
        <div className="mt-4 space-y-4 text-[15px] leading-relaxed text-charcoal/70">
          {children}
        </div>
      </section>
    </Reveal>
  );
}

export function LegalPage({ kind }) {
  const isPrivacy = kind === "privacy";
  return (
    <>
      <Seo
        title={isPrivacy ? "Privacy Policy | Steven Philips & Associates Limited" : "Terms of Use | Steven Philips & Associates Limited"}
        description={
          isPrivacy
            ? "How Steven Philips & Associates Limited collects, uses, and protects personal information submitted through its website."
            : "Terms of use for the Steven Philips & Associates Limited website."
        }
      />
      <LegalHero eyebrow="Legal" title={isPrivacy ? "Privacy Policy" : "Terms of Use"} />
      <div className="bg-white py-16 lg:py-24">
        <div className="container-x max-w-3xl">
          {isPrivacy ? (
            <>
              <Section title="Information We Collect">
                <p>
                  When you submit a consultation inquiry through our website, we
                  collect the information you provide: your full name, company or
                  organization (if provided), business email address, phone number
                  (if provided), the service you are interested in, and the details
                  of your message.
                </p>
              </Section>
              <Section title="How We Use Your Information">
                <p>
                  Your information is used solely to respond to your inquiry,
                  provide the advisory services you request, and maintain
                  appropriate business records. We do not sell, rent, or trade your
                  personal information to third parties.
                </p>
              </Section>
              <Section title="Consent">
                <p>
                  By submitting an inquiry and accepting this Privacy Policy, you
                  consent to our processing of the information you provide for the
                  purposes described above. You may withdraw your consent or request
                  the deletion of your information at any time by writing to us.
                </p>
              </Section>
              <Section title="Data Storage and Security">
                <p>
                  Inquiries submitted through this website are stored securely and
                  handled with appropriate technical and organizational measures to
                  protect against unauthorized access, alteration, or disclosure.
                  Access is limited to personnel who need it to respond to your
                  inquiry.
                </p>
              </Section>
              <Section title="Cookies and Analytics">
                <p>
                  This website does not use advertising or unnecessary third-party
                  tracking. Basic, privacy-respecting analytics may be used in
                  aggregate to understand overall site usage and improve the
                  experience for visitors.
                </p>
              </Section>
              <Section title="Data Retention">
                <p>
                  We retain inquiry information for as long as necessary to respond
                  to you and to meet legitimate business and record-keeping needs,
                  after which it is securely disposed of.
                </p>
              </Section>
              <Section title="Your Rights">
                <p>
                  You may request access to, correction of, or deletion of the
                  personal information we hold about you. To exercise these rights,
                  contact us using the details below.
                </p>
              </Section>
              <Section title="Contact">
                <p>
                  For any privacy questions or requests, please contact us at{" "}
                  <a
                    href={`mailto:${FIRM.email}`}
                    className="font-semibold text-navy underline decoration-gold"
                  >
                    {FIRM.email}
                  </a>{" "}
                  or call{" "}
                  <a href={FIRM.phoneHref} className="font-semibold text-navy underline decoration-gold">
                    {FIRM.phone}
                  </a>
                  .
                </p>
              </Section>
            </>
          ) : (
            <>
              <Section title="Acceptance of Terms">
                <p>
                  By accessing or using the Steven Philips &amp; Associates Limited
                  website, you agree to these Terms of Use. If you do not agree,
                  please discontinue use of the website.
                </p>
              </Section>
              <Section title="Use of the Website">
                <p>
                  You agree to use this website lawfully and respectfully, and not
                  to attempt to disrupt its operation, gain unauthorized access to
                  its systems, or submit false, misleading, or abusive information
                  through its forms.
                </p>
              </Section>
              <Section title="No Professional Advice">
                <p>
                  The content of this website is provided for general information
                  only. It does not constitute professional, financial, tax,
                  legal, or management advice, and it should not be relied upon as
                  a substitute for tailored advisory engagement. Formal advice is
                  provided only through a signed engagement with the firm.
                </p>
              </Section>
              <Section title="Intellectual Property">
                <p>
                  All content on this website — including text, design, graphics,
                  and the firm's name and marks — belongs to Steven Philips &amp;
                  Associates Limited or its licensors and may not be reproduced
                  without prior written permission.
                </p>
              </Section>
              <Section title="Limitation of Liability">
                <p>
                  To the fullest extent permitted by law, Steven Philips &amp;
                  Associates Limited accepts no liability for any loss or damage
                  arising from the use of, or reliance on, this website or its
                  content.
                </p>
              </Section>
              <Section title="Third-Party Links">
                <p>
                  This website may reference external websites or services. We are
                  not responsible for the content or practices of third-party
                  sites.
                </p>
              </Section>
              <Section title="Governing Law">
                <p>
                  These Terms are governed by the laws of the Federal Republic of
                  Nigeria.
                </p>
              </Section>
              <Section title="Changes to These Terms">
                <p>
                  We may update these Terms of Use from time to time. Continued use
                  of the website after changes are published constitutes acceptance
                  of the revised Terms.
                </p>
              </Section>
              <Section title="Contact">
                <p>
                  Questions about these Terms may be directed to{" "}
                  <a
                    href={`mailto:${FIRM.email}`}
                    className="font-semibold text-navy underline decoration-gold"
                  >
                    {FIRM.email}
                  </a>
                  .
                </p>
              </Section>
            </>
          )}
        </div>
      </div>
    </>
  );
}

export default function Privacy() {
  return <LegalPage kind="privacy" />;
}
